/**
 * Clean Code Culture attribution lockup for the public auth pages (CLE-385).
 *
 * The supplied PNG already carries the "clean code culture" wordmark, so the
 * only text rendered here is the "created by" prefix — repeating the company
 * name beside the logo would print it twice. The prefix stays English in both
 * locales because it reads as part of the brand lockup, like `Firmio` itself.
 *
 * The whole lockup links out to the company landing page.
 */

export const CLEAN_CODE_CULTURE_URL = 'https://cleancodeculture.com';

/** Intrinsic size of `/brand/clean-code-culture.png`, used to reserve the box. */
const LOGO_RATIO = 1024 / 541;

type LogoSize = 'sm' | 'md';

/** Rendered logo height in px; the wordmark is stacked on three lines, so it
 * needs more height than a single-line mark to stay legible. */
const HEIGHT: Record<LogoSize, number> = { sm: 34, md: 44 };

interface CleanCodeCultureLogoProps {
  size?: LogoSize;
  className?: string;
}

export function CleanCodeCultureLogo({ size = 'md', className = '' }: CleanCodeCultureLogoProps) {
  const height = HEIGHT[size];
  const width = Math.round(height * LOGO_RATIO);

  return (
    <a
      href={CLEAN_CODE_CULTURE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2.5 transition-opacity hover:opacity-80 ${className}`}
    >
      <span
        className="text-xs font-medium tracking-wide"
        style={{ color: '#94A3B8', fontFamily: "'Bricolage Grotesque', 'Manrope', sans-serif" }}
      >
        created by
      </span>
      <img
        src="/brand/clean-code-culture.png"
        alt="Clean Code Culture"
        width={width}
        height={height}
        style={{ height, width }}
        className="shrink-0"
      />
    </a>
  );
}
